import { AllPostsIcon, DropdownMenu } from '@buffer-mono/popcorn'

import { getPostTypeLabel } from '~publish/helpers/postType'
import type { FilterablePostType } from '~publish/helpers/postType'

import type { JSX } from 'react'

export type PostTypeFilterSubMenuProps = {
  /** Formats the channel's service can be filtered by, in display order. */
  options: readonly FilterablePostType[]
  selectedPostTypes: string[]
  onPostTypeSelect: (postTypes: string[]) => void
}

/**
 * Post format filter for the narrow channel header, nested in `MoreActions`.
 *
 * The wide header uses `~publish/components/FilterByPostType`. This variant
 * exists because that one is a standalone trigger, and the narrow header
 * collapses every filter into one menu.
 *
 * No search input, unlike the tag submenu: the list is three rows and never
 * scrolls, so the cmdk translate guard that list needs does not apply here.
 */
export function PostTypeFilterSubMenu({
  options,
  selectedPostTypes,
  onPostTypeSelect,
}: PostTypeFilterSubMenuProps): JSX.Element {
  const toggle = (postType: FilterablePostType): void => {
    onPostTypeSelect(
      selectedPostTypes.includes(postType)
        ? selectedPostTypes.filter((selected) => selected !== postType)
        : [...selectedPostTypes, postType],
    )
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <AllPostsIcon /> Filter by post type
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        {options.map((postType) => (
          <DropdownMenu.CheckboxItem
            key={postType}
            checked={selectedPostTypes.includes(postType)}
            onSelect={(event): void => {
              // Keep the menu open so a second format can be picked without
              // reopening it, matching the wide Combobox variant.
              event.preventDefault()
              toggle(postType)
            }}
          >
            {getPostTypeLabel(postType)}
          </DropdownMenu.CheckboxItem>
        ))}
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}
